/**
 * The fixture `DashboardSource` behind `/__ui/dashboard` — no session, no api, no database, so a
 * served build can be hit-tested against the production geometry
 * (`discovery/ui-reference/dashboards/`).
 *
 * The widgets it holds are the ones this account actually runs: a chart of a status column, a
 * multi-series column chart, a battery and a numbers tile. The chart's settings are a **real**
 * `settings_str` from the corpus, parsed through the registry, so the harness cannot drift from
 * what an imported widget would look like.
 */
import { chartWidget, widgetRegistry, type WidgetLayout } from '@elizmonday/shared';
import { statusColorHex } from '@elizmonday/ui';
import { useMemo, useState } from 'react';
import type { CanvasSettingsOptions, DashboardSource, WidgetData, WidgetRow } from './types';

/** `1801862111/37599009 Expenses by Phase` — a pie of a status column, summed over time tracking. */
const PIE_SETTINGS_STR =
  '{"x_axis_columns":{"status":true},"y_axis_columns":{"time_tracking":true},"z_axis_columns":{},' +
  '"guideline_base":{},"graph_type":"pie","empty_values":false,"group_by":"month",' +
  '"flat_legend_type":"flat_legend_type_percentage_value","createdBy":3247457,"calc_function_type":"sum"}';

/** `873813780/13762656 Activities' Status` — a column chart counting items by status. */
const COLUMN_SETTINGS_STR =
  '{"x_axis_columns":{"status":true},"y_axis_columns":{"default-label-count":true},"z_axis_columns":{},' +
  '"guideline_base":{},"graph_type":"column","empty_values":false,"group_by":"month","show_comulative_data":true}';

/**
 * The four labels of the sandbox board's status column, with the hexes F7's palette resolves for
 * them — the same values `bucketsOf` hands the real canvas. No raw hex outside the tokens
 * package (M0-F7 U1).
 */
const STATUS_BUCKETS = [
  { key: '0', label: 'Working on it', color: statusColorHex('working_orange'), itemCount: 7 },
  { key: '1', label: 'Done', color: statusColorHex('done_green'), itemCount: 12 },
  { key: '2', label: 'Stuck', color: statusColorHex('stuck_red'), itemCount: 3 },
  { key: '5', label: 'To Load', color: statusColorHex('bright_blue'), itemCount: 5 },
];

function widget(
  id: string,
  registryId: string,
  name: string,
  settings: Record<string, unknown>,
  layout: WidgetLayout,
): WidgetRow {
  const definition = widgetRegistry.get(registryId);
  return { id, registryId, kind: definition.kind, name, settings, layout };
}

export const FIXTURE_WIDGETS: WidgetRow[] = [
  widget('1000000000001', 'chart', 'Activity types', chartWidget.fromMondaySettings(COLUMN_SETTINGS_STR) as never, {
    x: 0,
    y: 0,
    w: 6,
    h: 6,
  }),
  widget('1000000000002', 'chart', 'Expenses by Phase', chartWidget.fromMondaySettings(PIE_SETTINGS_STR) as never, {
    x: 6,
    y: 0,
    w: 6,
    h: 6,
  }),
  widget('1000000000003', 'battery', 'Annual Goal', { columnId: 'status', boardIds: [], showPercentage: true, extra: {} }, {
    x: 0,
    y: 6,
    w: 6,
    h: 6,
  }),
  widget('1000000000004', 'numbers', 'Open items', { columnId: null, func: 'count', boardIds: [], unit: null, extra: {} }, {
    x: 6,
    y: 6,
    w: 3,
    h: 3,
  }),
];

/** The sandbox board the fixture widgets read, and the pickers its settings panes offer. */
const FIXTURE_BOARD_IDS = ['1801862111'] as const;

const FIXTURE_SETTINGS_OPTIONS: CanvasSettingsOptions = {
  boards: [{ id: '1801862111', name: 'Expenses' }],
  dimensionColumns: [
    { boardId: '1801862111', id: 'status', title: 'Status', type: 'status' },
    { boardId: '1801862111', id: 'person', title: 'Owner', type: 'people' },
    { boardId: '1801862111', id: 'date4', title: 'Date', type: 'date' },
  ],
  measureColumns: [
    { boardId: '1801862111', id: 'numbers', title: 'Amount', type: 'numbers' },
    { boardId: '1801862111', id: 'time_tracking', title: 'Time tracking', type: 'time-tracking' },
  ],
};

const FIXTURE_DATA: Record<string, WidgetData> = {
  '1000000000001': {
    buckets: STATUS_BUCKETS.map((bucket) => ({ ...bucket, values: { 'default-label-count': bucket.itemCount } })),
    itemCount: 27,
    truncated: false,
  },
  '1000000000002': {
    buckets: STATUS_BUCKETS.map((bucket) => ({
      ...bucket,
      values: { time_tracking: bucket.itemCount * 3600 },
    })),
    itemCount: 27,
    truncated: false,
  },
  '1000000000003': {
    buckets: STATUS_BUCKETS.map((bucket) => ({ ...bucket, values: { count: bucket.itemCount } })),
    itemCount: 27,
    truncated: false,
  },
  '1000000000004': {
    buckets: [{ key: '__all__', label: '', itemCount: 27, values: { value: 27 } }],
    itemCount: 27,
    truncated: false,
  },
};

/** The fixture source. `readOnly` drops the write affordances, which is the empty-canvas case. */
export function useFixtureDashboardSource(options: { empty?: boolean; readOnly?: boolean } = {}): DashboardSource {
  const [rows, setRows] = useState<WidgetRow[]>(options.empty === true ? [] : FIXTURE_WIDGETS);
  const [data] = useState(FIXTURE_DATA);
  const [filter, setFilter] = useState<unknown>(null);

  return useMemo<DashboardSource>(
    () => ({
      parent: { kind: 'board_view', id: '280306910' },
      boardIds: FIXTURE_BOARD_IDS,
      useWidgets: () => ({ data: rows, isPending: false }),
      useWidgetData: (widgetId: string) => ({ data: data[widgetId], isPending: false }),
      useSettingsOptions: () => ({ data: FIXTURE_SETTINGS_OPTIONS, isPending: false }),
      mutations:
        options.readOnly === true
          ? null
          : {
              add(registryId) {
                const definition = widgetRegistry.get(registryId);
                setRows((current) => [
                  ...current,
                  widget(
                    `10000000001${current.length}`,
                    registryId,
                    definition.title,
                    definition.defaultSettings as Record<string, unknown>,
                    { ...definition.defaultLayout, y: current.length * 6 },
                  ),
                ]);
              },
              move(layouts) {
                setRows((current) =>
                  current.map((row) => {
                    const next = layouts.find((entry) => entry.id === row.id);
                    return next === undefined ? row : { ...row, layout: { x: next.x, y: next.y, w: next.w, h: next.h } };
                  }),
                );
              },
              remove(widgetId) {
                setRows((current) => current.filter((row) => row.id !== widgetId));
              },
              rename(widgetId, name) {
                setRows((current) => current.map((row) => (row.id === widgetId ? { ...row, name } : row)));
              },
              update(widgetId, settings) {
                setRows((current) => current.map((row) => (row.id === widgetId ? { ...row, settings } : row)));
              },
            },
      filter: { value: filter, set: setFilter },
      reload: () => undefined,
      // The harness has no api and no socket: §6.2's refresh has nothing to invalidate.
      refresh: () => undefined,
    }),
    [rows, data, filter, options.readOnly],
  );
}
